import React, { useEffect, useState } from 'react';
import { FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface ContestData {
  contest_name: string;
  contest_date: string;
  result_date: string | null;
  registration_opens_at: string;
  no_of_questions: number;
  time_for_answer: number;
  entry_fee: string;
  reward: string;
  reward_worth: number;
  total_slots: number;
  registration_count: number;
}

interface Props {
  contestData: ContestData;
}

const ContestDetails: React.FC<Props> = ({ contestData }) => {
  const data = contestData;
  const [isRegisterLocked, setIsRegisterLocked] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    evaluateLockState(contestData.registration_opens_at, contestData.contest_date);
  }, [contestData]);

  const evaluateLockState = (registrationOpens: string, contestDate: string) => {
    const now = new Date().getTime();
    const regTime = new Date(registrationOpens?.replace(' ', 'T') || '').getTime();
    const contestTime = new Date(contestDate?.replace(' ', 'T') || '').getTime();

    if (now >= regTime && now < contestTime) {
      setIsRegisterLocked(false);
    } else {
      setIsRegisterLocked(true);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleRegisterClick = () => {
    // const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = localStorage.getItem('authToken');
    if (token && data) {
      navigate('/contest-registration', { state: { contest: data } });
    } else {
      navigate('/login');
    }
  };



  return (
    <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* Left Section */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-6 md:p-8 md:w-1/3">
          <div className="mb-6">
            <div className="text-2xl font-bold mb-1 capitalize">{data.contest_name}</div>
            <div className="text-3xl font-extrabold">Contest</div>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3">
              <span className="text-xl">📝</span>
              <span>Registration Opens: {formatDate(data.registration_opens_at)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl">📅</span>
              <span>Contest Date: {formatDate(data.contest_date)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl">📊</span>
              <span>Result Date: {formatDate(data.result_date)}</span>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 p-4 rounded-lg backdrop-blur-sm">
            <div className="text-sm font-semibold mb-1">Prize</div>
            <div className="text-2xl font-bold">{data.reward} worth ₹{data.reward_worth}</div>
          </div>
        </div>

        {/* Right Section */}
        <div className="p-6 md:p-8 md:w-2/3">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Contest Details</h3>

          <ul className="space-y-3 mb-6">
            <li className="flex items-start">
              <span className="mr-2 mt-1">•</span>
              <span>{data.no_of_questions} aptitude questions to be answered in {data.time_for_answer} seconds</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 mt-1">•</span>
              <span>Entry fee: ₹{data.entry_fee} (payable via GPay or PhonePe)</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 mt-1">•</span>
              <span>Limited to {data.total_slots} registrations — {data.registration_count} already registered</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2 mt-1">•</span>
              <span>Winner determined by highest score and fastest completion time</span>
            </li>
          </ul>

          <button
            aria-disabled={isRegisterLocked}
            role="button"
            onClick={isRegisterLocked ? undefined : handleRegisterClick}
            disabled={isRegisterLocked}
            className={`w-full md:w-auto px-6 py-3 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 
              ${isRegisterLocked
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
          >
            {isRegisterLocked ? <><FaLock className="text-sm" /> Locked</> : 'Register Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContestDetails;
